import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Hourglass, Network, Plus, Menu, Eye, TreeDeciduous, Grid3x3, Sprout, Inbox, Activity, LogOut } from "lucide-react";
import { toast } from "sonner";
import { TeamPanel } from "@/components/TeamPanel";
import { NotificationBell } from "@/components/NotificationBell";
import { ROLE_LABEL, isReviewer, canCreateAny } from "@/lib/perms";
import { CATEGORIES } from "@/lib/constants";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { WorldCtx } from "@/context/WorldContext";
import { WorldSidebar } from "@/components/WorldSidebar";
import { ShareDialog } from "@/components/ShareDialog";
import { Loader } from "@/components/Loader";
import { FanonBanner, FanonSwitcher } from "@/components/FanonSwitcher";

const Tab = ({ to, end, icon: Icon, label, testId }) => (
  <NavLink to={to} end={end} data-testid={testId}
    className={({ isActive }) => `flex items-center gap-1.5 px-1.5 sm:px-3 py-1.5 rounded-full text-sm transition-colors ${
      isActive ? "bg-amber-500/10 text-amber-300 border border-amber-500/40" : "text-slate-400 border border-transparent hover:text-slate-100"}`}>
    <Icon className="w-4 h-4" /><span className="hidden xl:inline">{label}</span>
  </NavLink>
);

export default function WorldLayout({ mode }) {
  const { worldId, token, profileId } = useParams();
  const readOnly = mode === "public";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sheet, setSheet] = useState(false);
  const [counts, setCounts] = useState({});
  const reloadCounts = useCallback(() => { if (!readOnly) api.get(`/worlds/${worldId}/comment-counts`).then((r) => setCounts(r.data)).catch(() => {}); }, [readOnly, worldId]);
  const rootPath = readOnly ? `/share/${token}` : `/worlds/${worldId}`;
  const basePath = profileId ? `${rootPath}/fanon/${profileId}` : rootPath;
  const [fanon, setFanon] = useState({ profiles: [], limit: null });
  const reloadProfiles = useCallback(async () => {
    if (!readOnly) setFanon((await api.get(`/worlds/${worldId}/fanon`)).data);
  }, [readOnly, worldId]);

  const load = useCallback(async () => {
    const params = profileId ? { profile: profileId } : {};
    try {
      setErr("");
      if (readOnly) {
        const { data } = await api.get(`/public/${token}`, { params });
        setData(data);
        setFanon({ profiles: data.profiles || [], limit: null });
      } else {
        const [w, p, r, c] = await Promise.all([api.get(`/worlds/${worldId}`), api.get(`/worlds/${worldId}/pages`, { params }),
          api.get(`/worlds/${worldId}/relationships`, { params }), api.get(`/worlds/${worldId}/collections`), reloadProfiles()]);
        setData({ world: w.data, pages: p.data, relationships: r.data, collections: c.data });
      }
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [readOnly, token, worldId, profileId, reloadProfiles]);
  useEffect(() => { load(); reloadCounts(); }, [load, reloadCounts]);
  const profile = profileId ? fanon.profiles.find((p) => p.id === profileId) || { id: profileId, name: "Fanon profile" } : null;
  const scope = profile ? { profile: profile.id } : {};

  const leave = async () => {
    if (!window.confirm(`Leave ${data.world.name}? You'll need a new invite to come back.`)) return;
    try { await api.post(`/worlds/${worldId}/leave`); toast.success("You left the world"); nav("/worlds"); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="world-error">
      <p className="font-display text-4xl text-amber-100">Lost in the mists</p>
      <p className="text-slate-400">{err}</p>
      <Link to="/" className="text-amber-400 hover:underline">Return home</Link>
    </div>
  );
  if (!data) return <Loader />;

  return (
    <WorldCtx.Provider value={{ ...data, basePath, rootPath, readOnly, reload: load, commentCounts: counts, reloadCounts,
      profile, profiles: fanon.profiles, fanonLimit: fanon.limit, reloadProfiles, scope }}>
      <div className="relative z-10 min-h-screen flex flex-col" data-testid="world-layout">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-canvas/85 border-b border-line px-3 md:px-6 h-16 flex items-center gap-1.5 sm:gap-3">
          <Sheet open={sheet} onOpenChange={setSheet}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 text-slate-400" data-testid="mobile-sidebar-btn"><Menu className="w-5 h-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-panel border-line p-0 overflow-y-auto w-72">
              <SheetTitle className="font-display text-2xl text-amber-100 px-4 pt-5">{data.world.name}</SheetTitle>
              <WorldSidebar onNavigate={() => setSheet(false)} />
            </SheetContent>
          </Sheet>
          {!readOnly && (
            <Link to="/worlds" className="p-2 rounded-full text-slate-400 hover:text-amber-300 hover:bg-veil/5 transition-colors" data-testid="back-to-worlds-btn">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          )}
          <Link to={rootPath} className="hidden sm:block font-display text-xl md:text-2xl text-amber-100 truncate max-w-[40vw]" data-testid="world-title-link">{data.world.name}</Link>
          <FanonSwitcher />
          {readOnly && <span className="hidden sm:flex items-center gap-1 text-[10px] font-code uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5"><Eye className="w-3 h-3" /> Read-only</span>}
          <nav className="flex items-center sm:gap-1 ml-auto">
            <Tab to={basePath} end icon={BookOpen} label="Codex" testId="tab-codex" />
            <Tab to={`${basePath}/timeline`} icon={Hourglass} label="Timeline" testId="tab-timeline" />
            <Tab to={`${basePath}/map`} icon={Network} label="Relationships" testId="tab-map" />
            <Tab to={`${basePath}/family`} icon={TreeDeciduous} label="Family" testId="tab-family" />
            <Tab to={`${basePath}/chart`} icon={Grid3x3} label="Shipping Chart" testId="tab-chart" />
            <Tab to={`${basePath}/tree-of-life`} icon={Sprout} label="Tree of Life" testId="tab-tree-of-life" />
          </nav>
          {!readOnly && (
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="hidden md:inline text-[10px] font-code uppercase tracking-widest text-amber-300 border border-amber-500/30 rounded-full px-2 py-0.5" data-testid="world-role-badge">{ROLE_LABEL[data.world.my_role]}</span>
              <Tab to={`${basePath}/pending`} icon={Inbox} label={`Pending${data.world.pending_count ? ` (${data.world.pending_count})` : ""}`} testId="tab-pending" />
              {data.world.pending_count > 0 && <span className="xl:hidden -ml-2 text-[10px] font-bold text-amber-400" data-testid="pending-count">{data.world.pending_count}</span>}
              <Tab to={`${basePath}/activity`} icon={Activity} label="Activity" testId="tab-activity" />
              {isReviewer(data.world) && <TeamPanel />}
              {data.world.my_role === "owner" ? <ShareDialog /> : (
                <button onClick={leave} title="Leave world" className="p-2 rounded-full text-slate-500 hover:text-rose-400" data-testid="leave-world-btn"><LogOut className="w-4 h-4" /></button>
              )}
              <NotificationBell />
              {canCreateAny(data.world, CATEGORIES.map((c) => c.key)) && (
                <Button size="sm" onClick={() => nav(`${basePath}/new`)} className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent" data-testid="new-page-btn">
                  <Plus className="w-4 h-4" /><span className="hidden sm:inline">Page</span>
                </Button>
              )}
            </div>
          )}
        </header>
        <FanonBanner />
        {!readOnly && !data.world.owner_pro && data.world.my_role !== "owner" && (
          <p className="px-6 py-2 text-center text-xs text-amber-200 bg-amber-500/10 border-b border-amber-500/30" data-testid="collab-paused-banner">Editing, comments and suggestions are paused because the owner's Pro plan has ended. You can still read everything.</p>
        )}
        <div className="flex flex-1">
          <aside className="hidden lg:block w-72 shrink-0 border-r border-line bg-panel/60 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
            <WorldSidebar />
          </aside>
          <main className="flex-1 min-w-0"><Outlet /></main>
        </div>
      </div>
    </WorldCtx.Provider>
  );
}
